import React from 'react';
import * as R from 'ramda';

import {
  BooleanField,
  FunctionField,
  ImageField,
  Labeled,
  SelectField,
  TextField,
  UrlField,
  useQuery,
} from 'react-admin';
import { authServer } from 'auth/keycloak';
import { Box, Grid, Typography } from '@material-ui/core';

import {
  cohortTimezones,
  formats,
  languages,
  partTimeHours,
  tracksB2b,
  b2bProjectTypes,
} from 'data';
import { DateField } from 'resources/common';
import { hasNewStudentPortal } from 'utils/hasNewStudentPortal';
import ContentAccessButton from 'resources/common/ContentAccessButton';
import PreviewButton from 'resources/common/PreviewButton';
import StudentPortalButton from 'resources/common/StudentPortalButton';
import CoursePublicationChip from 'resources/common/CoursePublicationChip';
import CourseLastPublishedField from 'resources/common/CourseLastPublishedField';
import CourseOpenEdxIdField from 'resources/common/CourseOpenEdxIdField';
import useCoursePublicationWatch from 'resources/common/useCoursePublicationWatch';
import { hasDoubleSessions } from 'resources/cohorts_b2b/b2bSessions';

const CompanyLogos = ({ record }) => (
  <>
    {R.map(({ url, file_name }) => (
      <ImageField
        key={url}
        source="url"
        title="file_name"
        record={{ url, file_name }}
        label="title"
      />
    ))(record.banner.company_logos)}
  </>
);

const formatTimetableDays = R.pipe(
  R.pathOr('0000000', ['timetable', 'days']),
  (days) => [
    days[1] === '1' ? 'Mon' : null,
    days[2] === '1' ? 'Tue' : null,
    days[3] === '1' ? 'Wed' : null,
    days[4] === '1' ? 'Thu' : null,
    days[5] === '1' ? 'Fri' : null,
    days[6] === '1' ? 'Sat' : null,
    days[0] === '1' ? 'Sun' : null,
  ],
  R.reject(R.isNil),
  R.join(' '),
);

const formatSessions = (record) => {
  const sessions = Number(R.prop('sessions', record));
  const sessionCount = sessions || (hasDoubleSessions(record) ? 2 : 1);
  return `${sessionCount} session${sessionCount === 1 ? '' : 's'}`;
};

const InformationTab = ({ record, resource }) => {
  const { liveRecord, onGithubOpen } = useCoursePublicationWatch({
    record,
    resource,
  });
  const courseWelcomeEmailTemplateId =
    liveRecord?.course?.email_templates?.welcome_email_template_id;
  const usesNewStudentPortal = hasNewStudentPortal(
    liveRecord?.id || liveRecord?._id,
    liveRecord?.start_date,
  );
  const { data: cohortSettings } = useQuery(
    {
      type: 'getOne',
      resource: 'cohort_settings',
      payload: {
        id: liveRecord?.id || liveRecord?._id,
      },
    },
    { enabled: Boolean(liveRecord?.id || liveRecord?._id) },
  );
  const multilanguageRecord = usesNewStudentPortal
    ? {
        multilanguage_enabled: Boolean(
          cohortSettings?.multilanguageEnabled ??
          cohortSettings?.multilanguage_enabled,
        ),
      }
    : R.hasPath(['feature_flags', 'multilanguage_enabled'], liveRecord)
      ? liveRecord
      : { feature_flags: { multilanguage_enabled: false } };
  const { data: emailTemplate, loading: emailTemplateLoading } = useQuery({
    type: 'getOne',
    resource: 'emails',
    payload: {
      pagination: {
        page: 1,
        perPage: 10,
      },
      sort: {
        field: 'id',
        order: 'ASC',
      },
      id: courseWelcomeEmailTemplateId,
    },
  });

  return (
    <Box p="16px">
      <Grid container>
        <Grid item xs={12}>
          <Typography variant="h5" gutterBottom>
            Cohort
          </Typography>
        </Grid>
        <Grid item xs={12} md={2}>
          <Labeled label="Company">
            <TextField source="company" record={liveRecord} />
          </Labeled>
        </Grid>
        <Grid item xs={12} md={2}>
          <Labeled label="Track">
            <SelectField source="track" choices={tracksB2b} record={liveRecord} />
          </Labeled>
        </Grid>
        <Grid item xs={12} md={2}>
          <Labeled label="Type of project">
            <SelectField
              source="project_type"
              choices={b2bProjectTypes}
              record={liveRecord}
            />
          </Labeled>
        </Grid>
        <Grid item xs={12} md={2}>
          <Labeled label="Format">
            <SelectField source="format" choices={formats} record={liveRecord} />
          </Labeled>
        </Grid>
        <Grid item xs={12} md={2}>
          <Labeled label="Timezone">
            <SelectField
              source="timezone"
              choices={cohortTimezones}
              record={liveRecord}
            />
          </Labeled>
        </Grid>
        <Grid item xs={12} md={2}>
          <Labeled label="Language">
            <SelectField
              source="language"
              choices={languages}
              record={liveRecord}
            />
          </Labeled>
        </Grid>
        <Grid item xs={12} md={2}>
          <Labeled label="Week hours">
            <TextField source="week_hours" record={liveRecord} />
          </Labeled>
        </Grid>
        <Grid item xs={12}>
          <Box mt="2em">
            <Typography variant="h5" gutterBottom>
              Dates
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Labeled label="Start Date">
            <DateField source="start_date" record={liveRecord} />
          </Labeled>
        </Grid>
        <Grid item xs={12} md={6}>
          <Labeled label="End Date">
            <DateField source="end_date" record={liveRecord} />
          </Labeled>
        </Grid>
        <Grid item xs={12} md={3}>
          <Labeled label="Days">
            <FunctionField record={liveRecord} render={formatTimetableDays} />
          </Labeled>
        </Grid>
        <Grid item xs={12} md={3}>
          <Labeled label="Sessions">
            <FunctionField record={liveRecord} render={formatSessions} />
          </Labeled>
        </Grid>
        {!R.propEq('format', 'pt', liveRecord) && (
          <Grid item xs={12} md={3}>
            <Labeled label="Start Time">
              <SelectField
                source="timetable.time.0.start_time"
                choices={partTimeHours}
                optionText="id"
                record={liveRecord}
              />
            </Labeled>
          </Grid>
        )}
        {!R.propEq('format', 'pt', liveRecord) && (
          <Grid item xs={12} md={2}>
            <Labeled label="End Time">
              <SelectField
                source="timetable.time.0.end_time"
                choices={partTimeHours}
                optionText="id"
                record={liveRecord}
              />
            </Labeled>
          </Grid>
        )}
        <Grid item xs={12}>
          <Box mt="2em">
            <Typography variant="h5" gutterBottom>
              Staff
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={2}>
          <Labeled label="PM">
            <FunctionField
              source="staff.pm"
              record={liveRecord}
              render={R.pipe(
                R.pathOr({}, ['staff', 'pm']),
                R.props(['first_name', 'last_name']),
                R.join(' '),
              )}
            />
          </Labeled>
        </Grid>
        <Grid item xs={12} md={2}>
          <Labeled label="Number of TAs">
            <TextField source="staff.ta_amount" record={liveRecord} />
          </Labeled>
        </Grid>
        <Grid item xs={12} md={2}>
          <Labeled label="Teachers">
            <FunctionField
              record={liveRecord}
              render={R.pipe(
                R.pathOr([], ['staff', 'teachers']),
                R.map(
                  R.pipe(
                    R.props(['first_name', 'last_name']),
                    R.join(' '),
                    R.trim,
                  ),
                ),
                R.join(', '),
                R.trim,
              )}
            />
          </Labeled>
        </Grid>
        <Grid item xs={12}>
          <Box mt="2em" mb="10px">
            <Typography variant="h5" gutterBottom>
              Courses
            </Typography>
          </Box>
        </Grid>
        <Box flex={1} flexDirection="row" mr={{ md: '2em' }}>
          {R.has('prework', liveRecord) && (
            <Box>
              <Box mb="10px" display="flex" alignItems="center">
                <Typography variant="h6" component="span">
                  Prework
                </Typography>
                <CoursePublicationChip
                  status={R.path(['prework', 'publication', 'status'], liveRecord)}
                  loading={!R.path(['prework', 'publication'], liveRecord)}
                />
              </Box>
              <Box>
                <Labeled label="Student Portal Title">
                  <TextField source="prework.title" record={liveRecord} />
                </Labeled>
              </Box>
              <CourseOpenEdxIdField record={liveRecord} source="prework" />
              <CourseLastPublishedField record={liveRecord} source="prework" />
              <Box mt="10px">
                <ContentAccessButton record={liveRecord} source="prework" onGithubOpen={onGithubOpen} />
              </Box>
              <Box mt="10px">
                <PreviewButton record={liveRecord} source="prework" />
              </Box>
            </Box>
          )}
        </Box>
        <Box flex={1} flexDirection="row" mr={{ md: '2em' }}>
          <Box mb="10px" display="flex" alignItems="center">
            <Typography variant="h6" component="span">
              Main Course
            </Typography>
            <CoursePublicationChip
              status={R.path(['course', 'publication', 'status'], liveRecord)}
              loading={!R.path(['course', 'publication'], liveRecord)}
            />
          </Box>
          <Box>
            <Labeled label="Student Portal Title">
              <TextField source="course.title" record={liveRecord} />
            </Labeled>
          </Box>
          <CourseOpenEdxIdField record={liveRecord} source="course" />
          <CourseLastPublishedField record={liveRecord} source="course" />
          <Box mt="10px">
            <ContentAccessButton record={liveRecord} source="course" onGithubOpen={onGithubOpen} />
          </Box>
          <Box mt="10px">
            <PreviewButton record={liveRecord} source="course" />
          </Box>
          <Box mt="10px">
            <StudentPortalButton record={liveRecord} />
          </Box>
        </Box>
        <Box flex={1} flexDirection="row" mr={{ md: '2em' }}>
          {R.has('career_services', liveRecord) && (
            <Box>
              <Box mb="10px" display="flex" alignItems="center">
                <Typography variant="h6" component="span">
                  Career Services
                </Typography>
                <CoursePublicationChip
                  status={R.path(
                    ['career_services', 'publication', 'status'],
                    liveRecord,
                  )}
                  loading={
                    !R.path(['career_services', 'publication'], liveRecord)
                  }
                />
              </Box>
              <Box>
                <Labeled label="Student Portal Title">
                  <TextField source="career_services.title" record={liveRecord} />
                </Labeled>
              </Box>
              <CourseOpenEdxIdField record={liveRecord} source="career_services" />
              <CourseLastPublishedField
                record={liveRecord}
                source="career_services"
              />
              <Box mt="10px">
                <ContentAccessButton record={liveRecord} source="career_services" onGithubOpen={onGithubOpen} />
              </Box>
              <Box mt="10px">
                <PreviewButton record={liveRecord} source="career_services" />
              </Box>
            </Box>
          )}
        </Box>
      {R.propEq('track', 'cy', liveRecord) && (
        <>
          <Grid item xs={12}>
            <Box mt="2em">
              <Typography variant="h5" gutterBottom>
                External IDs
              </Typography>
            </Box>
          </Grid>
        </>
      )}
      <Grid item xs={12}>
        <Box mt="2em">
          <Typography variant="h5" gutterBottom>
            Cohort specifics
            {usesNewStudentPortal && (
              <Box component="sup" ml={1}>
                <Box
                  component="span"
                  px={1}
                  py={0.25}
                  borderRadius="12px"
                  border={1}
                  borderColor="primary.main"
                  bgcolor="primary.main"
                  color="primary.contrastText"
                  fontSize="0.62em"
                  fontWeight={700}
                  letterSpacing="0.04em"
                  lineHeight={1.15}
                  textTransform="uppercase"
                >
                  New Student Portal
                </Box>
              </Box>
            )}
          </Typography>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <Box display={{ sm: 'block', md: 'flex' }}>
          {authServer.isRoles(['admin', 'gm', 'pm', 'outcomes']) &&
            !emailTemplateLoading && (
              <Box flexBasis="32%" mr={{ md: '2em' }}>
                <Labeled label="Course Welcome Email">
                  <Typography>
                    {R.ifElse(
                      R.has('name'),
                      (template) =>
                        `${template.name}${
                          template.language
                            ? ` - ${template.language.toUpperCase()}`
                            : ''
                        }`,
                      R.always('--'),
                    )(emailTemplate)}
                  </Typography>
                </Labeled>
              </Box>
            )}
          <Box flexBasis="68%">
            <Labeled label="Link to FAQ">
              {liveRecord.faq_url ? (
                <UrlField source="faq_url" record={liveRecord} />
              ) : (
                <Typography>--</Typography>
              )}
            </Labeled>
          </Box>
        </Box>
        <Box item xs={12} md={2}>
          <Box display="flex" alignItems="flex-start">
            <Labeled label="Zoom URL">
              {liveRecord.zoom_url ? (
                <UrlField source="zoom_url" record={liveRecord} />
              ) : (
                <Typography>--</Typography>
              )}
            </Labeled>
            <Box ml={3}>
              <Labeled label="Automatic Attendance enabled">
                <BooleanField
                  record={
                    R.hasPath(
                      ['feature_flags', 'automatic_attendance_enabled'],
                      liveRecord,
                    )
                      ? liveRecord
                      : {
                          feature_flags: {
                            automatic_attendance_enabled: false,
                          },
                        }
                  }
                  source="feature_flags.automatic_attendance_enabled"
                />
              </Labeled>
            </Box>
          </Box>
        </Box>
        <Box item xs={12} md={2}>
          <Labeled label="Slack URL">
            {liveRecord.slack_url ? (
              <UrlField source="slack_url" record={liveRecord} />
            ) : (
              <Typography>--</Typography>
            )}
          </Labeled>
        </Box>
        <Box item xs={12} md={2}>
          <Labeled label="Onboarding Video">
            {liveRecord.presentation_video ? (
              <UrlField source="presentation_video" record={liveRecord} />
            ) : (
              <Typography>--</Typography>
            )}
          </Labeled>
        </Box>
        <Box flexBasis="68%">
          <Labeled label="Accredible design ID">
            {liveRecord.external_ids?.accredible_design_id ? (
              <TextField
                source="external_ids.accredible_design_id"
                record={liveRecord}
              />
            ) : (
              <Typography>--</Typography>
            )}
          </Labeled>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <Labeled label="Text for client logos">
          <TextField source="banner.copy" record={liveRecord} emptyText="--" />
        </Labeled>
      </Grid>
      <Grid item xs={12}>
        <Labeled label="Client logos">
          {R.pathSatisfies(R.length, ['banner', 'company_logos'], liveRecord) ? (
            <CompanyLogos record={liveRecord} />
          ) : (
            <Typography>--</Typography>
          )}
        </Labeled>
      </Grid>
      {authServer.isRoles(['admin']) && (
        <Grid item xs={12} md={10}>
          <Labeled label="Multilanguage enabled">
            <BooleanField
              record={multilanguageRecord}
              source={
                usesNewStudentPortal
                  ? 'multilanguage_enabled'
                  : 'feature_flags.multilanguage_enabled'
              }
            />
          </Labeled>
        </Grid>
      )}
      </Grid>
    </Box>
  );
};

export default InformationTab;
